import React, { useRef } from "react";
import { ingestService, legacyImporterService, setupService, userService } from "../../Core/Api/services";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../Components/Misc/ListPageHeader";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { Autocomplete, Button, Grid, OutlinedInput, Paper, Stack, TextField, Typography } from "@mui/material";
import { ProviderAndStorage } from "../../Core/Api/Types/provider-and-storage";
import { DbIngestConfigPackInfos } from "../../Core/Api/Types/db-ingest-config-pack-infos";
import { True, UserHasAcl } from "../../Utils/helpers";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import { produce } from "immer";
import DataTable, { CellSize } from "../../Components/Table/table";
import { GridColDef } from "@mui/x-data-grid";
import { StorageKind } from "../../Core/Api/Types/storage-kind";
import { ImportType } from "../../Core/Api/Types/import-type";
import { ModalContext } from "../Modals";
import ModalHttpUpload, { ModalHttpUploadProps } from "../Modals/ModalHttpUpload/modal-http-upload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DownloadDoneOutlinedIcon from "@mui/icons-material/DownloadDoneOutlined";
import moment from "moment";
import { useTranslation } from "react-i18next";

export interface ExcelProperties {
  $type: string;
  fileName: string;
  uploadDate: Date;
  storageId: number | null;
}

export interface ImportProps {
  context: ImportType;
}

const Importer: React.FC<ImportProps> = (props) => {
  const { t } = useTranslation();
  const { context } = props;
  const enqueueSnackbar = useCustomSnackbar();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const uploadInputRef = useRef<any>(undefined);
  const [files, setFiles] = React.useState<ExcelProperties[]>([]);
  const [providers, setProviders] = React.useState<ProviderAndStorage[]>([]);
  const [packs, setPacks] = React.useState<DbIngestConfigPackInfos[]>([]);
  const [tokens, setTokens] = React.useState<number>(0);
  const [storageId, setStorageId] = React.useState<number>(-500);
  const [reports, setReports] = React.useState<
    | {
        $type: string | null;
        line: number | null;
        message: string | null;
        rescode: number | null;
        possibleValues: any | null;
        tag: string | null;
      }[]
    | null
  >(null);

  const [loadExcel, setLoadExcel] = React.useState<{
    $type: string | null;
    message: string | null;
    objectId: number;
    rescode: number;
    rowsCount: number;
    success: boolean;
  } | null>(null);

  const [checksOk, setChecksOk] = React.useState<{ titles: boolean; assets: boolean; assetsImported: boolean }>({
    titles: false,
    assets: false,
    assetsImported: false,
  });
  const [operationLock, setOperationLock] = React.useState<boolean>(false);

  const [request, setRequest] = React.useState<{
    props: ExcelProperties | null;
    firstRow: number | null;
    lastRow: number | null;
    providerId: number | null;
    isTest: boolean; // Production is false
    configIngestPackId: number | null;
    mode: string | null;
    kind: string | null;
  }>({
    props: null,
    firstRow: context === ImportType.LegacyImport ? 6 : 3,
    lastRow: 0,
    providerId: null,
    isTest: false, // Production is false
    configIngestPackId: null,
    mode: "",
    kind: "",
  });

  const [isLoading, setIsLoading] = React.useState(true);

  const LoadExcels = () => {
    legacyImporterService
      .Find(context === ImportType.LegacyImport ? StorageKind.LegacyImporter : StorageKind.BulkMetadataImporter)
      .then(function (response) {
        if (!response.success || response.results.length === 0) return;

        setFiles(response.results);
        if (response.results.length > 1) {
          response.results.sort(function (a, b) {
            return a.uploadDate > b.uploadDate ? -1 : a.uploadDate < b.uploadDate ? 1 : 0;
          });
        }
        setRequest(
          produce((draft) => {
            draft.props = response.results.find(True);
          })
        );
      });
  };

  const Init = () => {
    setRequest(
      produce((draft) => {
        draft.props = null;
        draft.firstRow = context === ImportType.LegacyImport ? 6 : 3;
        draft.lastRow = 0;
        draft.providerId = null;
        draft.isTest = false; // Production is false
        draft.configIngestPackId = null;
        draft.mode = "";
        draft.kind = "";
      })
    );
    setChecksOk(
      produce((draft) => {
        draft.assetsImported = false;
        draft.assets = false;
        draft.titles = false;
      })
    );
    setOperationLock(true);
    setLoadExcel(null);
    setIsLoading(true);
    LoadExcels();

    legacyImporterService.GetTokenCounter().then(function (response) {
      if (response.success) setTokens(response.objectId);
    });

    setupService.GetAllProviders().then(function (response) {
      if (response.success) setProviders(response.providersAndStorages as ProviderAndStorage[]);
    });

    ingestService.GetConfigPackList().then(function (response) {
      if (response.success) setPacks(response.packs as DbIngestConfigPackInfos[]);
    });

    setupService.GetAllStorages().then(function (response) {
      if (!response.success || !response.storages) return;

      let kind: StorageKind =
        context === ImportType.LegacyImport ? StorageKind.LegacyImporter : StorageKind.BulkMetadataImporter;
      let storageId = response.storages.find((x) => x.kind === kind)?.id ?? -500;
      setStorageId(storageId);

      if (storageId === -500)
        enqueueSnackbar(
          t("alert:warning.noStorageFoundforImporter", {
            context: context === ImportType.LegacyImport ? "legacy" : "bulkMetadata",
          }),
          "warning"
        );
    });
  };

  React.useEffect(Init, [context]);

  const tokenAdd = () => {
    legacyImporterService.AddTokenCounter().then(function (response) {
      if (response.success) {
        setTokens(tokens + 1);
        enqueueSnackbar(t("alert:success.newCreditTokenAdded"), "success");
      }
    });
  };

  const load = () => {
    legacyImporterService.Load(request?.props!).then(function (response) {
      setLoadExcel(response);
      if (!response.success) return;
      setRequest(
        produce((draft) => {
          draft.lastRow = response.rowsCount;
        })
      );

      setOperationLock(false);
    });
  };

  const operation = (mode, kind) => {
    let myRequest = JSON.parse(JSON.stringify(request));
    myRequest.mode = mode;
    myRequest.kind = kind;
    myRequest.type = context;

    setOperationLock(true);

    legacyImporterService.Operation(myRequest).then(function (response) {
      setLoadExcel(response);
      // to prevent multiple import of same excel.
      let lockOperation = false;
      if (!response.success) return;

      if (
        mode === "Check" &&
        kind === "Titles" &&
        response.result.reports.filter((x) => x.tag === "Error").length === 0
      ) {
        setChecksOk(
          produce((draft) => {
            draft.titles = true;
          })
        );
        enqueueSnackbar(t("alert:success.titleCheckReportCorrectExcel"), "success");
      } else if (
        mode === "Import" &&
        kind === "Titles" &&
        response.result.reports.filter((x) => x.tag === "Error").length === 0
      ) {
        enqueueSnackbar(t("alert:success.titleCheckReportCorrectExcel"), "success");
      } else if (
        mode === "Check" &&
        kind === "Assets" &&
        response.result.reports.filter((x) => x.tag === "Error").length === 0
      ) {
        setChecksOk(
          produce((draft) => {
            draft.assets = true;
          })
        );
        enqueueSnackbar(t("alert:success.assetCheckReportCorrectExcel"), "success");
      } else if (
        mode === "Import" &&
        kind === "Assets" &&
        response.result.reports.filter((x) => x.tag === "Error").length === 0
      ) {
        setChecksOk(
          produce((draft) => {
            draft.assetsImported = true;
          })
        );
        enqueueSnackbar(t("alert:success.assetImportReportCorrectExcel"), "success");
        lockOperation = true;
        if (context == ImportType.MetadataImport) {
          Init();
          enqueueSnackbar(t("alert:success.excelDeleted"), "success");
        }
      } else
        enqueueSnackbar(
          t("alert:warning.operationFinishedWithErrors", { mode, kind, count: response.result.reports.length }),
          "warning"
        );
      setReports(response.result.reports);
      setOperationLock(lockOperation);
    });
  };

  const columnsAction: GridColDef[] = [
    {
      field: "tag",
      headerName: t("common:tag"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      valueGetter: (value: any, row: any) => row?.tag,
    },
    {
      field: "line",
      headerName: t("common:line"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      valueGetter: (value: any, row: any) => row?.line,
    },
    {
      field: "message",
      headerName: t("common:message"),
      flex: CellSize.xxlarge,
      minWidth: CellSize.xxlarge,
      renderCell: (data: any) => (
        <Typography p={2} variant="body2">
          {data.value}
        </Typography>
      ),
    },
    {
      field: "possibleValues",
      headerName: t("common:values"),
      flex: CellSize.large,
      valueGetter: (value: any, row: any) => row?.possibleValues,
    },
  ];

  return (
    <Stack sx={{ flex: "1 1 0%", overflowY: "scroll" }}>
      <ListPageHeader mb={3}>
        <ListPageHeaderLeft
          title={context === ImportType.LegacyImport ? t("common:legacyImporter") : t("common:bulkMetadata")}
        />
        <ListPageHeaderRight>
          {UserHasAcl(AclTypes.aclSuperUser) && context === ImportType.LegacyImport && (
            <Button onClick={() => tokenAdd()} variant="contained" color="primary">
              {t("button:addAuthorizationToken")}
            </Button>
          )}

          <Button
            onClick={() => uploadInputRef.current.click()}
            variant="contained"
            color="primary"
            startIcon={<FileUploadIcon />}
            disabled={storageId == -500}
          >
            <label style={{ cursor: "pointer" }} htmlFor="upload-file-but">
              {t("field:label.uploadExcel")}
            </label>
            <input
              value={[]}
              style={{ display: "none" }}
              id="upload-file-but"
              ref={uploadInputRef}
              accept={undefined}
              multiple={false}
              type="file"
              onClick={(e) => {
                e.isTrusted && e.preventDefault();
              }}
              onChange={(e: any) => {
                openModal &&
                  openModal<ModalHttpUploadProps>(ModalHttpUpload, {
                    assetParentName: "",
                    assetId: -500,
                    storageId: storageId,
                    assetName: context === ImportType.LegacyImport ? "legacy-importer" : "metadata-importer",
                    files: [...e.target.files],
                    closeModal: () => {},
                    onClose: LoadExcels,
                  });
              }}
            />
          </Button>
        </ListPageHeaderRight>
      </ListPageHeader>
      <Paper sx={{ p: 2, backgroundImage: "none" }}>
        <Typography variant="h4" color="text.tier">
          {context === ImportType.LegacyImport ? t("common:legacyImporter") : t("route:linkTitle.bulkMetadataImporter")}
        </Typography>
        <Grid container spacing={2} mt={3}>
          <Grid item md={context === ImportType.LegacyImport ? 2 : 4}>
            <Typography component="span" fontWeight={500} color="text.primary">
              {t("field:label.selectExcelFile")}
            </Typography>
            <Autocomplete
              value={request?.props}
              onChange={(e, value) =>
                setRequest(
                  produce((draft) => {
                    if (value) draft.props = value;
                  })
                )
              }
              componentsProps={{ popper: { style: { width: "fit-content" } } }}
              options={files}
              getOptionLabel={(option) => `${option.fileName} (${moment(option.uploadDate).format("DD/MM/YY HH:mm")})`}
              sx={{ width: "100%", mt: 2 }}
              disabled={!files}
              renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
            />
          </Grid>

          {context === ImportType.LegacyImport && (
            <>
              <Grid item md={2}>
                <Typography component="span" fontWeight={500} color="text.primary">
                  {t("field:label.selectProvider")}
                </Typography>
                <Autocomplete
                  value={providers.find((x) => x.provider?.id === request.providerId) || null}
                  onChange={(e, value) =>
                    setRequest(
                      produce((draft) => {
                        if (value) draft.providerId = value.provider!.id;
                      })
                    )
                  }
                  options={providers}
                  getOptionLabel={(option) => option.provider?.labName!}
                  sx={{ width: "100%", mt: 2 }}
                  renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                />
              </Grid>
              <Grid item md={2}>
                <Typography component="span" fontWeight={500} color="text.primary">
                  {t("field:label.selectIngestConfigPack")}
                </Typography>
                <Autocomplete
                  value={packs.find((x) => x.id === request.configIngestPackId) || null}
                  onChange={(e, value) =>
                    setRequest(
                      produce((draft) => {
                        if (value) draft.configIngestPackId = value.id;
                      })
                    )
                  }
                  options={packs}
                  getOptionLabel={(option) => option.name!}
                  sx={{ width: "100%", mt: 2 }}
                  renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                />
              </Grid>
            </>
          )}

          <Grid item md={context === ImportType.LegacyImport ? 1.5 : 2}>
            <Typography component="span" fontWeight={500} color="text.primary">
              {t("field:label.firstExcelLineNumber")}
            </Typography>
            <OutlinedInput
              label={t("common:standard")}
              size="medium"
              type="number"
              sx={{ mt: 2, width: "100%" }}
              value={request?.firstRow}
              onChange={(e) =>
                setRequest(
                  produce((draft) => {
                    draft.firstRow = parseInt(e.target.value);
                  })
                )
              }
            />
          </Grid>

          <Grid item md={context === ImportType.LegacyImport ? 1.5 : 2}>
            <Typography component="span" fontWeight={500} color="text.primary">
              {t("field:label.lastExcelLineNumber")}
            </Typography>
            <OutlinedInput
              label={t("common:standard")}
              size="medium"
              type="number"
              sx={{ mt: 2, width: "100%" }}
              value={request?.lastRow}
              onChange={(e) =>
                setRequest(
                  produce((draft) => {
                    draft.lastRow = parseInt(e.target.value);
                  })
                )
              }
            />
          </Grid>

          <Grid item md={context === ImportType.LegacyImport ? 1 : 2}>
            <Typography component="span" fontWeight={500} color="text.primary" mb={1}>
              {t("button:loadExcel")}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadDoneOutlinedIcon />}
              size="medium"
              sx={{ mt: 2, width: "100%" }}
              disabled={request.props === null}
              onClick={() => load()}
            >
              {t("common:load")}
            </Button>
          </Grid>

          <Grid item md={context === ImportType.LegacyImport ? 1 : 2}>
            <Typography component="span" fontWeight={500} color="text.primary" mb={1}>
              {t("button:downloadExcel")}
            </Typography>
            <Button
              onClick={() => {
                legacyImporterService.DownloadExcelFile(request.props!).then((result) => {
                  if (!result) {
                    enqueueSnackbar(t("alert:warning.failedToDownloadExcelFile"), "warning");
                    return;
                  }

                  LoadExcels();
                });
              }}
              variant="outlined"
              startIcon={<DownloadOutlinedIcon />}
              sx={{ mt: 2, width: "100%" }}
              disabled={storageId == -500}
            >
              <Typography>{t("common:download")}</Typography>
            </Button>
          </Grid>

          <Grid item md={context === ImportType.LegacyImport ? 1 : 2}>
            <Typography component="span" fontWeight={500} color="text.primary" mb={1}>
              {t("button:deleteExcel")}
            </Typography>
            <Button
              onClick={() => {
                legacyImporterService.DeleteExcelFiles(request.props!).then((result) => {
                  if (!result.success) {
                    enqueueSnackbar(t("alert:warning.failToDeleteExcel"), "warning");
                  } else {
                    LoadExcels();
                    enqueueSnackbar(t("alert:success.excelDeleted"), "success");
                  }
                });
              }}
              variant="contained"
              color="error"
              startIcon={<DeleteOutlineOutlinedIcon />}
              sx={{ mt: 2, width: "100%" }}
              disabled={storageId == -500}
            >
              <Typography>{t("common:delete")}</Typography>
            </Button>
          </Grid>

          {loadExcel !== null && loadExcel.success && (
            <Typography component="span" fontWeight={500} sx={{ color: "green", pt: 2.5, pl: 3 }} mb={1}>
              {t("alert:success.excelFileLoaded")}
            </Typography>
          )}

          {loadExcel !== null && !loadExcel.success && (
            <Typography component="span" fontWeight={500} sx={{ color: "red" }} mb={1}>
              {t("alert:error.excelFileCoundNotLoad", { message: loadExcel.message })}
            </Typography>
          )}

          {loadExcel != null && loadExcel.success && (
            <Grid container spacing={2} ml={0.5}>
              {context === ImportType.LegacyImport && (
                <>
                  <Grid item md={3}>
                    <Button
                      variant="outlined"
                      size="medium"
                      sx={{ mt: 2, width: "100%" }}
                      onClick={() => operation("Check", "Titles")}
                      disabled={request.props === null || operationLock}
                    >
                      {t("button:checkTitles")}
                    </Button>
                  </Grid>
                  <Grid item md={3}>
                    <Button
                      variant="contained"
                      size="medium"
                      sx={{ mt: 2, width: "100%" }}
                      onClick={() => operation("Import", "Titles")}
                      disabled={request.props === null || !checksOk.titles || operationLock}
                    >
                      {t("button:importTitles")}
                    </Button>
                  </Grid>
                </>
              )}

              <Grid item md={3}>
                <Button
                  variant="outlined"
                  size="medium"
                  sx={{ mt: 2, width: "100%" }}
                  onClick={() => operation("Check", "Assets")}
                  disabled={
                    context === ImportType.LegacyImport
                      ? request.props === null ||
                        request.configIngestPackId === null ||
                        request.providerId === null ||
                        operationLock
                      : request.props === null || operationLock
                  }
                >
                  {t("button:checkAssets")}
                </Button>
              </Grid>
              <Grid item md={3}>
                <Button
                  size="medium"
                  variant="contained"
                  sx={{ mt: 2, width: "100%" }}
                  onClick={() => operation("Import", "Assets")}
                  disabled={
                    context === ImportType.LegacyImport
                      ? request.props == null ||
                        request.configIngestPackId == null ||
                        request.providerId == null ||
                        !checksOk.assets ||
                        operationLock
                      : request.props == null || !checksOk.assets || operationLock
                  }
                >
                  {t("button:importAssetsWithToken", {
                    count: (request.lastRow ?? 0) - (request.firstRow != null ? request.firstRow - 1 : 0),
                    token: tokens,
                  })}
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>

        {reports !== null && reports.length > 0 && (
          <Stack flex={0.6} mt={3}>
            <Typography mb={2} variant="h4" color="text.tier">
              {t("component:body.operationReport")}
            </Typography>

            <DataTable
              noControls
              rows={reports}
              columns={columnsAction}
              rowHeight={614}
              getRowId={() => Math.random()}
              getRowHeight={() => "auto"}
            />
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};

export default Importer;
